import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Heart, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Empty, Loader } from "./Common";
import { Emblem } from "./Emblem";

const SHADES = ["#FFFFFF", "#FECDD3", "#FB7185", "#E11D48", "#F9A8D4", "#BE123C"];

function FloatingHearts({ count, burst }: { count: number; burst: number }) {
  const ambient = Math.min(8, count);
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-3xl" aria-hidden>
      {Array.from({ length: ambient }, (_, i) => (
        <Heart key={`a${i}`} className="prayer-heart absolute" style={{ left: `${6 + ((i * 41) % 88)}%`, bottom: "-12%", width: 14 + (i % 3) * 5, height: 14 + (i % 3) * 5, color: SHADES[i % SHADES.length], fill: SHADES[i % SHADES.length], animationDelay: `${i * 1.3}s`, animationDuration: `${7 + (i % 4)}s` }} />
      ))}
      <AnimatePresence>
        {burst > 0 && Array.from({ length: 12 }, (_, i) => (
          <motion.span key={`b${burst}-${i}`} className="absolute bottom-8 left-1/2" initial={{ x: 0, y: 0, opacity: 1, scale: 0.6 }}
            animate={{ x: (i - 6) * 22 + (i % 2 ? 10 : -10), y: -140 - (i % 4) * 30, opacity: 0, scale: 1.2, rotate: (i - 6) * 8 }} transition={{ duration: 1.8, ease: "easeOut" }}>
            <Heart style={{ color: SHADES[i % SHADES.length], fill: SHADES[i % SHADES.length], width: 18, height: 18, filter: "drop-shadow(0 1px 2px rgba(0,0,0,.2))" }} />
          </motion.span>
        ))}
      </AnimatePresence>
    </div>
  );
}

function PrayerCard({ p, onChange, onRemove }: { p: any; onChange: (p: any) => void; onRemove: (id: string) => void }) {
  const { user } = useAuth();
  const [burst, setBurst] = useState(0);
  const pray = async () => {
    setBurst((b) => b + 1);
    if (p.i_prayed) return;
    try { onChange((await api.post(`/prayers/${p.id}/pray`)).data); } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async () => { try { await api.delete(`/prayers/${p.id}`); onRemove(p.id); toast.success("Prayer request removed"); } catch (e) { toast.error(errMsg(e)); } };
  const moderate = async (action: string) => { try { await api.post(`/admin/prayers/${p.id}`, { action }); action === "approve" ? onChange({ ...p, status: "published" }) : onRemove(p.id); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <article className="relative overflow-hidden rounded-3xl border-4 border-double border-[#D9A5A5] bg-[#FFF5F3] p-6 sm:p-8" data-testid={`prayer-${p.id}`}
      style={{ boxShadow: "inset 0 0 50px rgba(225,140,150,.18), 0 10px 30px -12px rgba(120,40,60,.25)" }}>
      <FloatingHearts count={p.prayed_count} burst={burst} />
      <div className="relative flex items-center gap-3">
        <Emblem avatar={p.author?.avatar} size={38} />
        <div className="flex-1"><p className="font-semibold text-slate-900">{p.author?.display_name}</p><p className="text-xs text-[#9F5F6B]">Prayer request{p.status === "pending" ? " · awaiting review" : ""}</p></div>
        {(p.is_mine || user?.role === "admin") && <button onClick={del} data-testid={`prayer-delete-${p.id}`} aria-label="Delete prayer request" className="rounded-full p-2 text-slate-400 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>}
      </div>
      <p className="relative mt-4 whitespace-pre-wrap font-scripture text-lg leading-relaxed text-[#4A2A30]">{p.text}</p>
      <div className="relative mt-5 flex flex-wrap items-center gap-3">
        {p.status === "published" && (
          <button onClick={pray} data-testid={`prayer-pray-${p.id}`} aria-pressed={p.i_prayed}
            className={`inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold shadow transition-transform hover:scale-105 active:scale-95 ${p.i_prayed ? "bg-[#E11D48] text-white" : "bg-white text-[#BE123C] ring-1 ring-[#FBCFE8]"}`}>
            <Heart className={`h-4 w-4 ${p.i_prayed ? "fill-white" : ""}`} />{p.i_prayed ? "I prayed" : "I'll pray"}
          </button>
        )}
        <span className="text-sm text-[#9F5F6B]" data-testid={`prayer-count-${p.id}`}>{p.prayed_count} {p.prayed_count === 1 ? "person has" : "people have"} prayed</span>
        {user?.role === "admin" && p.status === "pending" && <span className="flex gap-2"><Button size="sm" data-testid={`prayer-approve-${p.id}`} onClick={() => moderate("approve")} className="rounded-full">Approve</Button><Button size="sm" variant="outline" data-testid={`prayer-reject-${p.id}`} onClick={() => moderate("reject")} className="rounded-full">Reject</Button></span>}
      </div>
    </article>
  );
}

export function PrayerWall() {
  const [list, setList] = useState<any[] | null>(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/prayers").then((r) => setList(r.data)); }, []);
  const post = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/prayers", { text });
      setList((l) => [data, ...(l || [])]); setText("");
      toast[data.status === "pending" ? "info" : "success"](data.status === "pending" ? "Thank you. A moderator will review it before it appears." : "Your prayer request is shared.");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const update = (p: any) => setList((l) => (l || []).map((x) => (x.id === p.id ? p : x)));
  return (
    <div className="space-y-6" data-testid="prayer-wall">
      <div className="rounded-3xl border border-[#F3C9CF] bg-white p-6">
        <p className="font-display text-2xl font-semibold text-slate-900">Share a prayer request</p>
        <p className="mt-1 text-sm text-slate-500">Members can tap a heart to let you know they prayed. Please keep private details safe.</p>
        <Textarea data-testid="prayer-input" value={text} maxLength={1000} onChange={(e) => setText(e.target.value)} placeholder="Please pray for…" className="mt-4 min-h-[100px] bg-[#FFFBFA] font-scripture text-lg" />
        <div className="mt-3 flex justify-end"><Button data-testid="prayer-submit" disabled={busy || text.trim().length < 8} onClick={post} className="rounded-full bg-[#E11D48] px-6 hover:bg-[#BE123C]">{busy ? "Sharing…" : "Share request"}</Button></div>
      </div>
      {list === null ? <Loader /> : list.length === 0 ? <Empty testId="prayer-empty" title="No prayer requests yet" body="Be the first to ask for prayer." /> : list.map((p) => <PrayerCard key={p.id} p={p} onChange={update} onRemove={(id) => setList((l) => (l || []).filter((x) => x.id !== id))} />)}
    </div>
  );
}
